import type { IBiomaterial, IWispBiosample } from '@zero-dash/types';
import {
  ANALYTE_COLOURS,
  DEFAULT_ASSAY_FILTER_STATE,
  INFORMED_MODE,
  UNKNOWN_MANIFEST_ID,
  WISP_BIOSAMPLE_ID_BATCH_SIZE,
} from '@/constants/PatientDashboard/patientTimeline';
import type { IAnalysisSet } from '@/types/Analysis/AnalysisSets.types';
import type { IBiosample } from '@/types/Analysis/Biosamples.types';
import { getTumourBiosample } from '@/utils/functions/biosamples/getTumourBiosample';
import type {
  ChartAssay,
  DiseaseBurdenFilters,
  IAssayFilterState,
  IDiseaseBurdenAnalyte,
  IDiseaseBurdenData,
  IDiseaseBurdenFetchOutcome,
  IDiseaseBurdenLine,
  IDiseaseBurdenPoint,
  IDiseaseBurdenSeries,
} from '@/types/PatientDashboard/PatientTimeline.types';
import {
  buildChartLaneBiomaterialIndex,
  resolveChartLanePointFields,
} from './ChartLane.util';

interface IAnalyteGroup {
  analyte: string;
  assays: Set<ChartAssay>;
  points: IDiseaseBurdenPoint[];
}

const getAnalysisSetBiosamples = (analysisSets: IAnalysisSet[]): IBiosample[] => (
  analysisSets.flatMap((analysisSet) => analysisSet.biosamples ?? [])
);

export const getDiseaseBurdenBiosampleIds = (analysisSets: IAnalysisSet[]): string[] => (
  Array.from(new Set(
    getAnalysisSetBiosamples(analysisSets).map((biosample) => biosample.biosampleId),
  ))
);

export const fetchWispResults = async (
  biosampleIds: string[],
  fetchBatch: (batch: string[]) => Promise<IWispBiosample[]>,
  signal?: AbortSignal,
): Promise<IDiseaseBurdenFetchOutcome> => {
  const batches: string[][] = [];
  for (let index = 0; index < biosampleIds.length; index += WISP_BIOSAMPLE_ID_BATCH_SIZE) {
    batches.push(biosampleIds.slice(index, index + WISP_BIOSAMPLE_ID_BATCH_SIZE));
  }

  const results: IWispBiosample[] = [];
  let failedCount = 0;

  await batches.reduce(async (previous, batch) => {
    await previous;
    if (signal?.aborted) {
      return;
    }
    try {
      results.push(...await fetchBatch(batch));
    } catch {
      failedCount += 1;
    }
  }, Promise.resolve());

  return { results, failedCount };
};

const buildInformedIndex = (analysisSets: IAnalysisSet[]): Map<string, boolean> => {
  const informedByAnalysisSetId = new Map<string, boolean>();
  analysisSets.forEach((analysisSet) => {
    const tumourBiosample = getTumourBiosample(analysisSet.biosamples ?? []);
    const hasTumour = tumourBiosample?.biosampleStatus === 'tumour';
    informedByAnalysisSetId.set(
      analysisSet.analysisSetId,
      informedByAnalysisSetId.get(analysisSet.analysisSetId) || hasTumour,
    );
  });
  return informedByAnalysisSetId;
};

export const buildDiseaseBurdenData = (
  wispResults: IWispBiosample[],
  analysisSets: IAnalysisSet[],
  biomaterials: IBiomaterial[],
): IDiseaseBurdenData => {
  const biomaterialById = buildChartLaneBiomaterialIndex(biomaterials);
  const informedByAnalysisSetId = buildInformedIndex(analysisSets);

  const biosampleById = new Map<string, IBiosample>();
  getAnalysisSetBiosamples(analysisSets).forEach((biosample) => {
    if (!biosampleById.has(biosample.biosampleId)) {
      biosampleById.set(biosample.biosampleId, biosample);
    }
  });

  const groupsByAnalyte = new Map<string, IAnalyteGroup>();

  wispResults.forEach((wisp) => {
    // A point needs a measured result; a missing one is not plotted.
    if (wisp.snvPurity === null || wisp.snvLod === null || wisp.detected === null) {
      return;
    }

    const biosample = biosampleById.get(wisp.biosampleId);
    if (!biosample) {
      return;
    }

    const isInformed = informedByAnalysisSetId.get(wisp.analysisSetId);
    if (isInformed === undefined) {
      return;
    }

    const dated = resolveChartLanePointFields(
      biosample,
      biomaterialById.get(biosample.biomaterialId),
    );
    if (!dated) {
      return;
    }

    const assays: ChartAssay[] = [dated.assay];
    const point: IDiseaseBurdenPoint = {
      id: `${wisp.analysisSetId}-${wisp.biosampleId}`,
      biosampleId: wisp.biosampleId,
      date: dated.date,
      purity: wisp.snvPurity,
      lod: wisp.snvLod,
      detected: wisp.detected,
      isInformed,
      assays,
      manifestId: dated.manifestId,
    };

    const group = groupsByAnalyte.get(dated.analyte);
    if (group) {
      group.points.push(point);
      assays.forEach((assay) => group.assays.add(assay));
    } else {
      groupsByAnalyte.set(
        dated.analyte,
        { analyte: dated.analyte, assays: new Set(assays), points: [point] },
      );
    }
  });

  const analytes: IDiseaseBurdenAnalyte[] = Array.from(groupsByAnalyte.values())
    .sort((a, b) => a.analyte.localeCompare(b.analyte))
    .map((group, index) => ({
      analyte: group.analyte,
      colour: ANALYTE_COLOURS[index % ANALYTE_COLOURS.length],
      assays: Array.from(group.assays).sort((a, b) => a.localeCompare(b)),
      points: [...group.points].sort((a, b) => a.date.localeCompare(b.date)),
    }));

  return { analytes };
};

const byDateThenId = (a: IDiseaseBurdenPoint, b: IDiseaseBurdenPoint): number => (
  a.date.localeCompare(b.date) || a.id.localeCompare(b.id)
);

const getRun = (point: IDiseaseBurdenPoint): string => (
  point.manifestId === UNKNOWN_MANIFEST_ID
    ? `${UNKNOWN_MANIFEST_ID}-${point.id}`
    : String(point.manifestId)
);

const splitRunIntoLines = (points: IDiseaseBurdenPoint[]): IDiseaseBurdenPoint[][] => {
  const pointsByPosition = new Map<number, IDiseaseBurdenPoint[]>();
  let currentDate = '';
  let position = 0;

  [...points].sort(byDateThenId).forEach((point) => {
    position = point.date === currentDate ? position + 1 : 0;
    currentDate = point.date;
    pointsByPosition.set(position, [...(pointsByPosition.get(position) ?? []), point]);
  });

  return Array.from(pointsByPosition.entries())
    .sort(([a], [b]) => a - b)
    .map(([, linePoints]) => linePoints);
};

export const getBurdenSeries = (line: IDiseaseBurdenLine): IDiseaseBurdenSeries[] => {
  const pointsByRun = new Map<string, IDiseaseBurdenPoint[]>();
  line.points.forEach((point) => {
    const run = getRun(point);
    pointsByRun.set(run, [...(pointsByRun.get(run) ?? []), point]);
  });

  return Array.from(pointsByRun.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .flatMap(([run, runPoints]) => splitRunIntoLines(runPoints).map((points, index) => ({
      key: `${line.assay}-${run}-${index}`,
      assay: line.assay,
      points,
    })));
};

export const reconcileDiseaseBurdenFilters = (
  data: IDiseaseBurdenData,
  previous: DiseaseBurdenFilters,
): DiseaseBurdenFilters => {
  const next: DiseaseBurdenFilters = {};
  data.analytes.forEach((analyte) => {
    const previousAssays = previous[analyte.analyte] ?? {};
    const assays: Record<string, IAssayFilterState> = {};
    analyte.assays.forEach((assay) => {
      assays[assay] = previousAssays[assay] ?? DEFAULT_ASSAY_FILTER_STATE;
    });

    const isUnchanged = Object.keys(assays).length === Object.keys(previousAssays).length
      && analyte.assays.every((assay) => previousAssays[assay] === assays[assay]);
    next[analyte.analyte] = isUnchanged ? previousAssays : assays;
  });
  return next;
};

export const getDiseaseBurdenLines = (
  analyte: IDiseaseBurdenAnalyte,
  filters: Record<string, IAssayFilterState> | undefined,
): IDiseaseBurdenLine[] => (
  analyte.assays
    .map((assay) => ({ assay, state: filters?.[assay] }))
    .filter((entry): entry is { assay: ChartAssay; state: IAssayFilterState } => (
      Boolean(entry.state?.isSelected)
    ))
    .map(({ assay, state }) => {
      const wantsInformed = state.mode === INFORMED_MODE.INFORMED;
      return {
        assay,
        points: analyte.points.filter(
          (point) => point.assays.includes(assay) && point.isInformed === wantsInformed,
        ),
      };
    })
);
